import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';

import { ClientDetails } from './ClientDetails';
import { Client, ClientAction } from './types';
import { getAction } from './utils';

export const ClientModal = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { toast } = useToast();

  const [deleteClient, { isLoading: isDeletingModel }] =
    api.endpoints.deleteClient.useMutation();

  const [checkBucket, { data: bucketInfoData, isLoading: isLoadingBucket }] =
    api.endpoints.getBucketInfo.useLazyQuery();

  const [createClient, { isLoading: isCreatingClient }] =
    api.endpoints.createClient.useMutation();

  const [updateClient, { isLoading: isUpdatingClient }] =
    api.endpoints.updateClient.useMutation();

  useEffect(() => {
    if (bucketInfoData) {
      setBucketInfo(bucketInfoData.data.message);
    }
  }, [bucketInfoData]);

  const [deleteInput, setDeleteInput] = useState('');
  const [bucketName, setBucketName] = useState('');
  const [bucketInfo, setBucketInfo] = useState('');
  const [message, setMessage] = useState('');

  const { action, client } = useSelector(
    slices.clients.selectors.selectConfiguration,
  );

  const stringifyClientId = client?.id?.toString();

  const isCreateClient = action === 'add';
  const isEditClient = action === 'edit';
  const isCheckBucket = action === 'checkBucket';
  const isViewingCredits = action === 'viewCredits';
  const isDeleteClient = action === 'delete';

  if (!action || isViewingCredits) {
    return null;
  }

  const handleOnHide = () => {
    if (isCheckBucket) {
      setBucketName('');
      setBucketInfo('');
    } else if (isDeleteClient) {
      setDeleteInput('');
    }
    dispatch(slices.clients.actions.resetConfiguration());
    setMessage('');
  };

  const handleActionConfirm = (configs?: any, message?: string) => {
    const queries = {
      delete: deleteClient,
      checkBucket: checkBucket,
      edit: updateClient,
      add: createClient,
    };

    const query = queries[action];

    query({
      clientId: stringifyClientId,
      bucketName: bucketName,
      configs,
    })
      ?.unwrap()
      .then(() => {
        if (isCheckBucket) {
          setBucketName('');
        } else {
          if (isCreateClient || isEditClient) {
            toast({
              message: message ?? '',
              status: 'success',
            });
          } else {
            toast({
              message: toastMessage,
              status: 'success',
            });
          }
          handleOnHide();
        }
      })
      .catch((error: any) => {
        if (isCheckBucket || isCreateClient || isEditClient) {
          if (error.response && error.response.status === 400) {
            setMessage(error.response.data[0] || error.response.data.name[0]);
            toast({
              message: error.response.data[0] || error.response.data.name[0],
              status: 'danger',
            });
          }
        } else {
          toast({
            message: intl.formatMessage(
              {
                id: 'ClientModal.actionFailedMessage',
                defaultMessage: 'Failed to {action} client. Please try again.',
                description:
                  'Error message when client action fails. Example: action = "delete"',
              },
              { action: getAction(action, intl) },
            ),
            status: 'danger',
          });
        }
      })
      .finally(() => (isDeleteClient ? handleOnHide() : null));
  };

  const resolveProps = ({
    action,
    client,
  }: {
    action: ClientAction;
    client: Nullable<Client>;
  }) => {
    const clientName = client?.name;

    switch (action) {
      case 'checkBucket':
        return {
          isFullPage: false,
          disabled: !bucketName,
          tooltip: !bucketName
            ? intl.formatMessage({
                id: 'ClientModal.bucketNameRequiredTooltip',
                defaultMessage: 'Bucket name is required',
                description: 'Tooltip when bucket name is required',
              })
            : undefined,
          variant: 'primary' as const,
          toastMessage: intl.formatMessage(
            {
              id: 'ClientModal.bucketCheckedMessage',
              defaultMessage: 'The bucket **{bucketName}** has been checked',
              description:
                'Success message when bucket is checked. Example: bucketName = "my-bucket"',
            },
            { bucketName },
          ),
          title: intl.formatMessage({
            id: 'ClientModal.checkBucketTitle',
            defaultMessage: 'Check Bucket',
            description: 'Modal title for checking bucket',
          }),
          children: (
            <FormField<string, false>
              statusMessage={
                bucketInfo.length > 0
                  ? {
                      message: bucketInfo,
                      status: 'info',
                    }
                  : undefined
              }
              input={TextInput}
              label=""
              name="check bucket"
              onChange={(value) => {
                if (!value) {
                  setBucketName('');
                } else {
                  const valid = value?.trim();
                  setBucketName(valid);
                }
              }}
              value={bucketName}
            />
          ),
        };
      case 'delete':
        return {
          disabled: deleteInput !== clientName,
          tooltip:
            deleteInput !== clientName
              ? intl.formatMessage({
                  id: 'ClientModal.clientNameMismatchTooltip',
                  defaultMessage: 'Client name does not match',
                  description:
                    'Tooltip when client name does not match for deletion',
                })
              : undefined,
          variant: 'danger' as const,
          toastMessage: intl.formatMessage(
            {
              id: 'ClientModal.clientDeletedMessage',
              defaultMessage: 'The client **{clientName}** has been deleted',
              description:
                'Success message when client is deleted. Example: clientName = "Test Client"',
            },
            { clientName },
          ),
          isFullPage: false,
          title: intl.formatMessage({
            id: 'ClientModal.deleteClientTitle',
            defaultMessage: 'Delete Client',
            description: 'Modal title for deleting client',
          }),
          children: (
            <FormField<string, false>
              input={TextInput}
              description={intl.formatMessage({
                id: 'ClientModal.deleteConfirmationDescription',
                defaultMessage:
                  'Type the name of the client to confirm deletion',
                description: 'Description for delete confirmation input',
              })}
              label=""
              name="Delete Client"
              onChange={(value) => {
                if (!value) {
                  setDeleteInput('');
                } else {
                  setDeleteInput(value);
                }
              }}
              value={deleteInput}
            />
          ),
        };
      default:
        return {
          disabled: undefined,
          tooltip: undefined,
          variant: undefined,
          isFullPage: false,
          toastMessage: '',
          children: null,
          title: '',
        };
    }
  };

  const {
    variant,
    isFullPage,
    toastMessage,
    tooltip,
    disabled,
    children,
    title,
  } = resolveProps({
    action,
    client,
  });

  const isLoading =
    isDeletingModel || isLoadingBucket || isCreatingClient || isUpdatingClient;

  const modalProps = {
    children,
    isVisible: true,
    isFullPage,
    toastMessage,
    title,
    primaryAction: {
      isLoading,
      disabled,
      tooltip,
      text: intl.formatMessage({
        id: 'ClientModal.confirmButton',
        defaultMessage: 'Confirm',
        description: 'Primary action button text',
      }),
      variant,
      onClick: handleActionConfirm,
    },
    onHide: handleOnHide,
    onCancel: handleOnHide,
  };

  return isEditClient || isCreateClient ? (
    <ClientDetails
      onHide={handleOnHide}
      onClick={handleActionConfirm}
      isLoading={isLoading}
      message={message}
    />
  ) : (
    <Modal {...modalProps} />
  );
};
